import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ChevronRight } from 'lucide-react';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

export interface BreadcrumbItem {
    label: string;
    to?: string;
}

export interface PageHeroProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    breadcrumb?: BreadcrumbItem[];
    imageUrl?: string;
    badge?: React.ReactNode;
    children?: React.ReactNode;
    className?: string;
}

// Default fallback image matches the hero image used on Über Uns
const DEFAULT_HERO_IMAGE = '/api/media/11111111-1111-1111-1111-0000000000d2';

export const PageHero: React.FC<PageHeroProps> = ({
    title,
    subtitle,
    breadcrumb,
    imageUrl,
    badge,
    children,
    className = '',
}) => {
    const bgImage = resolveAssetUrl(imageUrl || DEFAULT_HERO_IMAGE);

    return (
        <section className={`relative w-full py-16 sm:py-24 md:py-28 overflow-hidden bg-slate-900 flex items-center justify-center ${className}`}>
            {/* Background Cover Image */}
            <div className="absolute inset-0 z-0">
                <img
                    src={bgImage}
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-cover select-none"
                    onError={(e) => {
                        // Fallback if image fails to load
                        (e.target as HTMLElement).style.display = 'none';
                    }}
                />
                {/* Dark gradient overlay for optimal readability */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-black/80" />
            </div>

            {/* Hero Content */}
            <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 w-full text-center flex flex-col items-center">
                {/* Breadcrumbs */}
                {breadcrumb && breadcrumb.length > 0 && (
                    <motion.nav
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="inline-flex items-center gap-1.5 sm:gap-2 px-4 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/15 text-xs sm:text-sm text-white/90 mb-6 flex-wrap justify-center shadow-lg"
                        aria-label="Breadcrumb"
                    >
                        {breadcrumb.map((item, index) => {
                            const isLast = index === breadcrumb.length - 1;
                            return (
                                <React.Fragment key={index}>
                                    {index > 0 && (
                                        <ChevronRight className="w-3.5 h-3.5 text-white/50 shrink-0" />
                                    )}
                                    {item.to && !isLast ? (
                                        <Link
                                            to={item.to}
                                            className="hover:text-primary-foreground/90 text-white/80 transition-colors font-medium hover:underline"
                                        >
                                            {item.label}
                                        </Link>
                                    ) : (
                                        <span className={isLast ? "text-white font-semibold" : "text-white/80"}>
                                            {item.label}
                                        </span>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </motion.nav>
                )}

                {/* Badge if provided */}
                {badge && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                        className="mb-4"
                    >
                        {badge}
                    </motion.div>
                )}

                {/* Main Title */}
                <motion.h1
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white drop-shadow-md"
                >
                    {title}
                </motion.h1>

                {/* Subtitle / Description */}
                {subtitle && (
                    <motion.p
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="mt-4 text-base sm:text-lg md:text-xl text-white/85 max-w-2xl font-normal leading-relaxed"
                    >
                        {subtitle}
                    </motion.p>
                )}

                {/* Extra Slot */}
                {children && (
                    <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="mt-6 w-full flex justify-center"
                    >
                        {children}
                    </motion.div>
                )}
            </div>
        </section>
    );
};
